/**
 * Anonymous-canvas tRPC transport guard.
 *
 * Prevents `anonymousProject.*` ops from being batched with protected ops
 * (which 401 in middleware and poison the entire batch), and terminates
 * protected ops client-side when the visitor has no Clerk session — avoiding
 * network 401 noise and retry storms for anonymous canvas viewers.
 *
 * How it works:
 * 1. A `splitLink` at the top of the link chain routes ops whose path starts
 *    with `anonymousProject.` to a DEDICATED batch link (never mixed with
 *    other namespaces). This holds for authed users too (harmless — separate
 *    batch) so the behavior is deterministic.
 * 2. Non-anonymous ops pass through `signedOutGuardLink`, which checks
 *    whether the visitor is definitely signed out. If so, the op is
 *    terminated locally with a TRPCClientError shaped as UNAUTHORIZED — each
 *    hook lands in its normal error state silently instead of hitting the
 *    network. While Clerk is still loading (no session object yet, but a
 *    `__client_uat` cookie is present), ops pass through so the authed app's
 *    first render is not broken.
 */

import { TRPCClientError } from "@trpc/client";
import { observable } from "@trpc/server/observable";
import type { AnyRouter } from "@trpc/server/unstable-core-do-not-import";
import type { Operation, TRPCLink } from "@trpc/client";
import { hasSignedInClerkClient } from "@/lib/clerk/read-clerk";
import { isPublicProcedurePath } from "@/lib/trpc/publicProcedures";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Prefix that identifies the anonymous-project namespace. */
const ANON_PROJECT_PREFIX = "anonymousProject.";

/**
 * Error message used by the signed-out guard link. Distinct from
 * SESSION_EXPIRED_MESSAGE so sessionExpiredLink does not redirect anonymous
 * visitors to /sign-in.
 */
export const ANON_TRANSPORT_TERMINATED = "UNAUTHORIZED";

// ---------------------------------------------------------------------------
// Routing predicate
// ---------------------------------------------------------------------------

/** True when the operation targets the `anonymousProject.*` namespace. */
export function isAnonymousProjectOp(op: Operation): boolean {
  return op.path.startsWith(ANON_PROJECT_PREFIX);
}

/**
 * True when the operation targets a procedure that needs no Clerk session
 * (public share reads/comments, public YC app, claim-cookie anon writes). These
 * must bypass `signedOutGuardLink` — otherwise a logged-out visitor's public
 * share query is terminated client-side before it ever reaches the server — and
 * travel on their own batch so they never 401 alongside a protected op.
 */
export function isPublicProcedureOp(op: Operation): boolean {
  return isPublicProcedurePath(op.path);
}

// ---------------------------------------------------------------------------
// Signed-out detection
// ---------------------------------------------------------------------------

/**
 * True when the visitor is definitely signed out. The inverse of
 * `hasSignedInClerkClient` (`read-clerk.ts`), which owns the precedence: an
 * active session; then, ONLY on the anonymous canvas (`anonymousSurface.ts`),
 * Clerk's loaded state overrides the cookie; otherwise the `__client_uat`
 * cookie. Off the anonymous surface a loaded, sessionless client with a
 * non-zero cookie is an EXPIRED signed-in client and must stay "signed in"
 * so it gets the session-expired redirect.
 */
export function isDefinitelySignedOut(): boolean {
  if (typeof window === "undefined") return false;
  return !hasSignedInClerkClient();
}

// ---------------------------------------------------------------------------
// Guard link
// ---------------------------------------------------------------------------

/**
 * tRPC link that terminates non-anonymous ops locally when the visitor has no
 * Clerk session. Produces a TRPCClientError with code UNAUTHORIZED so each
 * React Query hook lands in its error state silently — no network request, no
 * 401, no retry storm.
 *
 * While Clerk is still loading (session not yet resolved but `__client_uat`
 * cookie present), ops pass through normally so the authed app's first render
 * is not broken.
 */
export function signedOutGuardLink<
  TRouter extends AnyRouter = AnyRouter,
>(): TRPCLink<TRouter> {
  return () =>
    ({ op, next }) =>
      observable((observer) => {
        // Server-side or signed-in / Clerk-loading: pass through
        if (typeof window === "undefined" || !isDefinitelySignedOut()) {
          const subscription = next(op).subscribe({
            next: (value) => observer.next(value),
            error: (err) => observer.error(err),
            complete: () => observer.complete(),
          });
          return () => subscription.unsubscribe();
        }

        // Definitely signed out: terminate locally with UNAUTHORIZED error.
        // Shape matches what the tRPC server would return so downstream
        // error handlers (React Query, component error boundaries) see a
        // familiar structure.
        const error = new TRPCClientError(ANON_TRANSPORT_TERMINATED, {
          result: {
            error: {
              message: ANON_TRANSPORT_TERMINATED,
              code: -32001, // TRPC_ERROR_CODES_BY_KEY.UNAUTHORIZED
              data: {
                code: "UNAUTHORIZED",
                httpStatus: 401,
                path: op.path,
              },
            },
          },
        });
        observer.error(error);
        return;
      });
}

// ---------------------------------------------------------------------------
// Retry suppression helper
// ---------------------------------------------------------------------------

/**
 * React Query retry function that suppresses retries for locally-terminated
 * UNAUTHORIZED errors. Without this, the default retry (3 attempts with
 * exponential backoff) would fire — each attempt would hit the same guard and
 * terminate immediately (no network), but the attempts are unnecessary noise.
 */
export function shouldRetry(failureCount: number, error: Error): boolean {
  if (
    error instanceof TRPCClientError &&
    error.message === ANON_TRANSPORT_TERMINATED
  ) {
    return false;
  }
  // Default React Query behavior: retry up to 3 times
  return failureCount < 3;
}
